import { Schema } from "effect"
import { HttpApiEndpoint, HttpApiGroup } from "effect/http-api"
import { Authentication } from "./AuthApi.ts"
import { FxRateUnavailable, InvalidFxRange } from "./Errors.ts"
import { CurrencyCode, IsoDate, PositiveDecimal } from "./Money.ts"

// FX rates for backtests in another currency (docs/design/0005, P1). Rates are
// ECB euro reference fixings; every pair is derived through EUR in BigDecimal.

/**
 * fresh: the last fixing is at most 7 days old. stale: older, so recent bars
 * can't be converted. missing: no fixings stored yet.
 */
export const FxCoverageStatus = Schema.Literals(["fresh", "stale", "missing"])
export type FxCoverageStatus = typeof FxCoverageStatus.Type

/** A currency an account or backtest can be converted to. EUR, the base, is always listed. */
export const FxCurrency = Schema.Struct({
  code: CurrencyCode,
  firstDate: Schema.NullOr(IsoDate),
  lastDate: Schema.NullOr(IsoDate),
  status: FxCoverageStatus
})
export type FxCurrency = typeof FxCurrency.Type

/**
 * Daily rates for one pair: `rates[i]` is units of `to` per 1 `from`, fixed on
 * `dates[i]` (ascending). The series starts with the fixing just before `start`,
 * so the first bar of a backtest has its previous-day rate.
 */
export const FxSeries = Schema.Struct({
  from: CurrencyCode,
  to: CurrencyCode,
  /** e.g. "ecb". */
  source: Schema.String,
  derivedVia: Schema.Literal("EUR"),
  dates: Schema.Array(IsoDate),
  rates: Schema.Array(PositiveDecimal)
})
export type FxSeries = typeof FxSeries.Type

export class FxApi extends HttpApiGroup.make("fx")
  .add(
    HttpApiEndpoint.get("currencies", "/currencies", { success: Schema.Array(FxCurrency) }),
    HttpApiEndpoint.get("series", "/series", {
      query: { from: CurrencyCode, to: CurrencyCode, start: IsoDate, end: IsoDate },
      success: FxSeries,
      error: [FxRateUnavailable, InvalidFxRange]
    })
  )
  .middleware(Authentication)
  .prefix("/fx")
{}
